import type {
  Db,
  Contact,
  Job,
  Proposal,
  Invoice,
  Activity,
  Sale,
  ContactSource,
  JobType,
  JobStatus,
  ProposalStatus,
  InvoiceStatus,
  ActivityType,
  LineItem,
} from "./types";

// SoCal street names and cities
const socalStreets = [
  "Palm Ave",
  "Ocean Blvd",
  "Sunset Dr",
  "Pacific Coast Hwy",
  "El Camino Real",
  "La Jolla Blvd",
  "Coronado Ave",
  "Mission Bay Dr",
  "Harbor Dr",
  "Coast Hwy",
  "Beach Blvd",
  "Newport Blvd",
  "Balboa Ave",
  "Rosecrans St",
  "Clairemont Dr",
  "Miramar Rd",
  "Torrey Pines Rd",
  "Camino Del Mar",
  "Via De La Valle",
  "Rancho Santa Fe Rd",
];

const socalCities = [
  "San Diego, CA",
  "Los Angeles, CA",
  "Irvine, CA",
  "Newport Beach, CA",
  "La Jolla, CA",
  "Carlsbad, CA",
  "Encinitas, CA",
  "Del Mar, CA",
  "Coronado, CA",
  "Oceanside, CA",
  "Huntington Beach, CA",
  "Laguna Beach, CA",
  "San Clemente, CA",
  "Dana Point, CA",
  "Temecula, CA",
  "Rancho Santa Fe, CA",
  "Escondido, CA",
  "Poway, CA",
  "Chula Vista, CA",
  "El Cajon, CA",
];

const firstNames = [
  "Michael",
  "Jennifer",
  "David",
  "Sarah",
  "Robert",
  "Lisa",
  "James",
  "Maria",
  "John",
  "Patricia",
  "William",
  "Linda",
  "Richard",
  "Barbara",
  "Joseph",
  "Elizabeth",
  "Thomas",
  "Susan",
  "Christopher",
  "Jessica",
  "Daniel",
  "Karen",
  "Matthew",
  "Nancy",
  "Anthony",
  "Betty",
  "Mark",
  "Margaret",
  "Donald",
  "Sandra",
];

const lastNames = [
  "Smith",
  "Johnson",
  "Williams",
  "Brown",
  "Jones",
  "Garcia",
  "Miller",
  "Davis",
  "Rodriguez",
  "Martinez",
  "Hernandez",
  "Lopez",
  "Gonzalez",
  "Wilson",
  "Anderson",
  "Thomas",
  "Taylor",
  "Moore",
  "Jackson",
  "Martin",
  "Lee",
  "Perez",
  "Thompson",
  "White",
  "Harris",
  "Sanchez",
  "Clark",
  "Ramirez",
  "Lewis",
  "Robinson",
];

// Roofing materials and descriptions for proposals
const roofingMaterials = [
  { name: "Owens Corning Duration Shingles", unitPrice: 125 },
  { name: "GAF Timberline HDZ Shingles", unitPrice: 135 },
  { name: "CertainTeed Landmark Pro Shingles", unitPrice: 145 },
  { name: "Boral Concrete Tile Roofing", unitPrice: 450 },
  { name: "Eagle Roofing Tile - Capistrano", unitPrice: 425 },
  { name: "Standing Seam Metal Roofing", unitPrice: 850 },
  { name: "Clay Tile Roofing - Spanish Style", unitPrice: 650 },
  { name: "Synthetic Slate Roofing", unitPrice: 550 },
];

const laborItems = [
  { name: "Tear-off and disposal of existing roof", unitPrice: 2.5 },
  { name: "Install synthetic underlayment", unitPrice: 0.75 },
  { name: "Install ice and water shield", unitPrice: 1.25 },
  { name: "Install drip edge flashing", unitPrice: 4.5 },
  { name: "Install ridge vent system", unitPrice: 12 },
  { name: "Install pipe boot flashings", unitPrice: 45 },
  { name: "Install step flashing at walls", unitPrice: 18 },
  { name: "Chimney flashing and counter-flashing", unitPrice: 350 },
];

const gutterItems = [
  { name: '5" Seamless Aluminum Gutters', unitPrice: 12 },
  { name: '6" Seamless Aluminum Gutters', unitPrice: 15 },
  { name: "Gutter Guards - LeafFilter style", unitPrice: 22 },
  { name: "Downspout - 2x3 Aluminum", unitPrice: 8 },
  { name: "Downspout - 3x4 Aluminum", unitPrice: 12 },
];

const sidingItems = [
  { name: "James Hardie Fiber Cement Siding", unitPrice: 12 },
  { name: "LP SmartSide Engineered Wood", unitPrice: 9 },
  { name: "Vinyl Siding - Premium Grade", unitPrice: 6 },
  { name: "Stucco Repair and Refinish", unitPrice: 8 },
];

const jobNotes = [
  "Customer mentioned water stains on ceiling in master bedroom. Suspect flashing issue around chimney.",
  "Previous roof installed in 2005. Multiple missing shingles visible from street.",
  "HOA requires architectural shingles in earth tones. Need to provide color samples.",
  "Customer wants to upgrade to cool roof for energy efficiency. Discuss Title 24 compliance.",
  "Insurance claim in progress. Need to coordinate with adjuster for inspection.",
  "Steep pitch roof (8/12). Will need additional safety equipment and labor.",
  "Customer has solar panels that need to be removed and reinstalled. Coordinate with solar company.",
  "Wood shake conversion to composite shingles. Check for rot damage during tear-off.",
  "Customer interested in adding skylights during roof replacement. Provide quote for 2 Velux units.",
  "Multi-level roof with 3 different sections. Complex flashing details required.",
  "Tile roof repair - several cracked tiles near ridge. Customer wants color-matched replacements.",
  "Flat roof section over garage. Recommend TPO or modified bitumen system.",
  "Customer reports persistent leak during Santa Ana winds. Likely wind-driven rain infiltration.",
  "Fascia boards showing rot damage. Include wood replacement in scope.",
  "Customer wants to add radiant barrier during re-roof for attic heat reduction.",
];

const activityDescriptions = {
  call: [
    "Discussed project timeline and material options with homeowner",
    "Followed up on proposal - customer comparing with other bids",
    "Confirmed inspection appointment for tomorrow at 10am",
    "Customer called asking about warranty coverage details",
    "Left voicemail regarding permit approval status",
    "Discussed financing options - customer interested in 12-month same as cash",
    "Customer requested additional color samples for shingles",
    "Confirmed material delivery date with supplier",
  ],
  email: [
    "Sent detailed proposal with material specifications",
    "Provided references from similar projects in the neighborhood",
    "Sent permit application copies for customer records",
    "Emailed project timeline and crew information",
    "Sent warranty documentation and care instructions",
    "Provided before/after photos from recent project",
    "Sent invoice and payment instructions",
    "Emailed completion certificate and final inspection report",
  ],
  note: [
    "Customer prefers morning start times due to work schedule",
    "Access gate code: 1234# - enter through side yard",
    "Large dog in backyard - customer will secure during work",
    "Neighbor at 123 Main also interested in roof estimate",
    "Customer mentioned potential kitchen remodel next year",
    "HOA approval letter received and filed",
    "Customer paid deposit via check #4521",
    "All materials delivered and staged in driveway",
  ],
  site_visit: [
    "Completed initial roof inspection - measured 2,400 sq ft, noted 3 areas of concern",
    "Met with insurance adjuster - agreed on scope of work",
    "Final walkthrough with customer - all items completed satisfactorily",
    "Progress check - tear-off complete, underlayment installed",
    "Delivered material samples for customer approval",
    "Re-measured for additional work on detached garage",
    "Inspected completed work with city building inspector - passed",
    "Post-storm inspection - no issues found with recent installation",
  ],
  status_change: [
    "Moved to Inspection Scheduled - appointment set for Monday",
    "Proposal sent to customer via email",
    "Customer accepted proposal - scheduling installation",
    "Job won - deposit received, ordering materials",
    "Work in progress - crew on site",
    "Job completed - final inspection passed",
    "Job lost - customer went with competitor on price",
    "Moved to negotiation - customer requested scope changes",
  ],
};

// Helper functions
function randomInt(min: number, max: number): number {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

function randomElement<T>(arr: T[]): T {
  return arr[Math.floor(Math.random() * arr.length)];
}

function randomDate(daysAgo: number): string {
  const date = new Date();
  date.setDate(date.getDate() - randomInt(0, daysAgo));
  date.setHours(randomInt(8, 18), randomInt(0, 59), 0, 0);
  return date.toISOString();
}

function generatePhone(): string {
  const areaCodes = ["619", "858", "760", "949", "714", "310", "562", "818"];
  return `(${randomElement(areaCodes)}) ${randomInt(200, 999)}-${String(randomInt(0, 9999)).padStart(4, "0")}`;
}

function generateEmail(name: string): string {
  const domains = [
    "gmail.com",
    "yahoo.com",
    "hotmail.com",
    "outlook.com",
    "icloud.com",
  ];
  const cleanName = name.toLowerCase().replace(/\s+/g, ".");
  return `${cleanName}${randomInt(1, 99)}@${randomElement(domains)}`;
}

function generateAddress(): string {
  const num = randomInt(100, 9999);
  return `${num} ${randomElement(socalStreets)}, ${randomElement(socalCities)} ${randomInt(90000, 92999)}`;
}

// Generator functions
function generateContacts(): Contact[] {
  const contacts: Contact[] = [];
  const sources: ContactSource[] = [
    "referral",
    "ad",
    "website",
    "door_knock",
    "repeat_customer",
  ];

  for (let i = 1; i <= 75; i++) {
    const firstName = randomElement(firstNames);
    const lastName = randomElement(lastNames);
    const name = `${firstName} ${lastName}`;

    contacts.push({
      id: i,
      name,
      phone: generatePhone(),
      email: generateEmail(name),
      address: generateAddress(),
      source: randomElement(sources),
      created_at: randomDate(365),
      job_count: 0,
      total_value: 0,
    });
  }

  return contacts.sort(
    (a, b) =>
      new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
  );
}

function generateSales(): Sale[] {
  return [
    {
      id: 1,
      first_name: "Jane",
      last_name: "Doe",
      email: "jane.doe@finturf.com",
      role: "admin",
      avatar: { src: "https://i.pravatar.cc/150?u=jane" },
    },
    {
      id: 2,
      first_name: "Mike",
      last_name: "Johnson",
      email: "mike.johnson@finturf.com",
      role: "manager",
      avatar: { src: "https://i.pravatar.cc/150?u=mike" },
    },
    {
      id: 3,
      first_name: "Sarah",
      last_name: "Williams",
      email: "sarah.williams@finturf.com",
      role: "sales_rep",
      avatar: { src: "https://i.pravatar.cc/150?u=sarah" },
    },
    {
      id: 4,
      first_name: "Carlos",
      last_name: "Garcia",
      email: "carlos.garcia@finturf.com",
      role: "sales_rep",
      avatar: { src: "https://i.pravatar.cc/150?u=carlos" },
    },
  ];
}

function generateJobs(contacts: Contact[], sales: Sale[]): Job[] {
  const jobs: Job[] = [];
  const jobTypes: JobType[] = [
    "roof_replacement",
    "roof_repair",
    "gutter",
    "siding",
    "window",
    "general",
  ];
  const statuses: JobStatus[] = [
    "new_lead",
    "inspection_scheduled",
    "proposal_sent",
    "negotiation",
    "won",
    "in_progress",
    "completed",
    "lost",
  ];

  // Track index per status for Kanban ordering
  const statusIndexCounters: Record<JobStatus, number> = {
    new_lead: 0,
    inspection_scheduled: 0,
    proposal_sent: 0,
    negotiation: 0,
    won: 0,
    in_progress: 0,
    completed: 0,
    lost: 0,
  };

  // Get sales reps and managers for assignment (not just admins)
  const assignableUsers = sales.filter(
    (s) => s.role === "sales_rep" || s.role === "manager",
  );

  for (let i = 1; i <= 75; i++) {
    const contact = randomElement(contacts);
    const jobType = randomElement(jobTypes);
    const roofSqft = randomInt(1200, 4500);
    const createdAt = randomDate(180);
    const updatedAt = new Date(createdAt);
    updatedAt.setDate(updatedAt.getDate() + randomInt(0, 30));
    const status = randomElement(statuses);

    // Estimate value based on job type and size
    let estimatedValue: number;
    switch (jobType) {
      case "roof_replacement":
        estimatedValue = roofSqft * randomInt(8, 15);
        break;
      case "roof_repair":
        estimatedValue = randomInt(500, 5000);
        break;
      case "gutter":
        estimatedValue = randomInt(1500, 8000);
        break;
      case "siding":
        estimatedValue = randomInt(8000, 35000);
        break;
      case "window":
        estimatedValue = randomInt(2000, 15000);
        break;
      default:
        estimatedValue = randomInt(1000, 10000);
    }

    // Assign index within status column
    const index = statusIndexCounters[status];
    statusIndexCounters[status]++;

    // Randomly assign to a sales rep or manager
    const assignedTo = randomElement(assignableUsers).id;

    jobs.push({
      id: i,
      contact_id: contact.id,
      assigned_to: assignedTo,
      job_type: jobType,
      status,
      address: contact.address,
      roof_sqft: roofSqft,
      estimated_value: Math.round(estimatedValue),
      notes: randomElement(jobNotes),
      created_at: createdAt,
      updated_at: updatedAt.toISOString(),
      index,
    });
  }

  return jobs.sort(
    (a, b) =>
      new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
  );
}

function generateProposals(jobs: Job[]): Proposal[] {
  const proposals: Proposal[] = [];
  const statuses: ProposalStatus[] = ["draft", "sent", "accepted", "declined"];

  // Only create proposals for jobs that are past new_lead stage
  const eligibleJobs = jobs.filter((j) => j.status !== "new_lead");

  for (let i = 1; i <= 75; i++) {
    const job = eligibleJobs[i % eligibleJobs.length];
    const lineItems: LineItem[] = [];

    // Add main material
    const material = randomElement(roofingMaterials);
    lineItems.push({
      description: material.name,
      quantity: Math.ceil(job.roof_sqft / 100),
      unit_price: material.unitPrice,
    });

    // Add labor items
    const laborCount = randomInt(2, 4);
    for (let l = 0; l < laborCount; l++) {
      const labor = randomElement(laborItems);
      lineItems.push({
        description: labor.name,
        quantity: job.roof_sqft,
        unit_price: labor.unitPrice,
      });
    }

    // Maybe add gutters
    if (Math.random() > 0.5) {
      const gutter = randomElement(gutterItems);
      lineItems.push({
        description: gutter.name,
        quantity: randomInt(80, 200),
        unit_price: gutter.unitPrice,
      });
    }

    const total = lineItems.reduce(
      (sum, item) => sum + item.quantity * item.unit_price,
      0,
    );

    const createdAt = new Date(job.created_at);
    createdAt.setDate(createdAt.getDate() + randomInt(1, 14));

    const validUntil = new Date(createdAt);
    validUntil.setDate(validUntil.getDate() + 30);

    proposals.push({
      id: i,
      job_id: job.id,
      line_items: lineItems,
      total: Math.round(total),
      status: randomElement(statuses),
      valid_until: validUntil.toISOString().split("T")[0],
      created_at: createdAt.toISOString(),
    });
  }

  return proposals;
}

function generateInvoices(jobs: Job[], proposals: Proposal[]): Invoice[] {
  const invoices: Invoice[] = [];
  const statuses: InvoiceStatus[] = ["pending", "paid", "overdue"];

  // Only create invoices for completed or in_progress jobs with accepted proposals
  const acceptedProposals = proposals.filter((p) => p.status === "accepted");
  const eligibleJobs = jobs.filter(
    (j) => j.status === "completed" || j.status === "in_progress",
  );

  const count = Math.min(75, acceptedProposals.length * 2, eligibleJobs.length);

  for (let i = 1; i <= count; i++) {
    const proposal = acceptedProposals[i % acceptedProposals.length];
    const job = jobs.find((j) => j.id === proposal.job_id) || eligibleJobs[0];
    const status = randomElement(statuses);

    const createdAt = new Date(proposal.created_at);
    createdAt.setDate(createdAt.getDate() + randomInt(1, 7));

    const dueDate = new Date(createdAt);
    dueDate.setDate(dueDate.getDate() + 30);

    let paidAt: string | null = null;
    if (status === "paid") {
      const paid = new Date(createdAt);
      paid.setDate(paid.getDate() + randomInt(1, 25));
      paidAt = paid.toISOString();
    }

    invoices.push({
      id: i,
      job_id: job.id,
      proposal_id: proposal.id,
      amount: proposal.total,
      status,
      due_date: dueDate.toISOString().split("T")[0],
      paid_at: paidAt,
    });
  }

  // Fill remaining if needed
  while (invoices.length < 75) {
    const i = invoices.length + 1;
    const job = randomElement(eligibleJobs);
    const proposal = randomElement(acceptedProposals);
    const status = randomElement(statuses);

    const dueDate = new Date();
    dueDate.setDate(dueDate.getDate() + randomInt(-30, 30));

    invoices.push({
      id: i,
      job_id: job.id,
      proposal_id: proposal.id,
      amount: randomInt(5000, 50000),
      status,
      due_date: dueDate.toISOString().split("T")[0],
      paid_at: status === "paid" ? new Date().toISOString() : null,
    });
  }

  return invoices;
}

function generateActivities(jobs: Job[], sales: Sale[]): Activity[] {
  const activities: Activity[] = [];
  const types: ActivityType[] = [
    "call",
    "email",
    "note",
    "site_visit",
    "status_change",
  ];

  for (let i = 1; i <= 75; i++) {
    const job = randomElement(jobs);
    const type = randomElement(types);
    const descriptions = activityDescriptions[type];

    const timestamp = new Date(job.created_at);
    timestamp.setDate(timestamp.getDate() + randomInt(0, 60));
    timestamp.setHours(randomInt(8, 18), randomInt(0, 59), 0, 0);

    // Activity is usually performed by the assigned rep, but sometimes by others
    const userId =
      Math.random() > 0.2
        ? job.assigned_to
        : randomElement(sales.filter((s) => s.role !== "admin")).id;

    activities.push({
      id: i,
      job_id: job.id,
      user_id: userId,
      type,
      description: randomElement(descriptions),
      timestamp: timestamp.toISOString(),
    });
  }

  return activities.sort(
    (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime(),
  );
}

export default (): Db => {
  const sales = generateSales();
  const contacts = generateContacts();
  const jobs = generateJobs(contacts, sales);

  // Compute aggregates for each contact
  contacts.forEach((contact) => {
    const contactJobs = jobs.filter((j) => j.contact_id === contact.id);
    contact.job_count = contactJobs.length;
    contact.total_value = contactJobs.reduce(
      (sum, j) => sum + j.estimated_value,
      0,
    );
  });

  const proposals = generateProposals(jobs);
  const invoices = generateInvoices(jobs, proposals);
  const activities = generateActivities(jobs, sales);

  return {
    sales,
    contacts,
    jobs,
    proposals,
    invoices,
    activities,
    configuration: [
      {
        id: 1,
        config: {} as Db["configuration"][number]["config"],
      },
    ],
  };
};
